import { useCallback, useState } from 'react';
import {
  useUploadFile as useUploadFileMutation,
  useVerifyCard as useVerifyCardMutation,
} from '@xhype/card-app-sdk';
import { useAuthStore } from '@package/smart-wallet/store';
import * as Sentry from '@sentry/react';

export const useHandleVerifyCard = () => {
  const id = useAuthStore((store) => store.id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  /* Mutations */
  const verifyCard = useVerifyCardMutation({
    onError: (error: any) => {
      console.log(`Verify card: Unexpected error - ${error.message}`);
      setError(error?.response?.data?.msg);
    },
    onSuccess: () => {
      console.log('Verify card: Successfully verified');
    },
  });

  const uploadFile = useUploadFileMutation({
    onError: (error: any) => {
      console.log(`Upload file: Unexpected error - ${error.message}`);
      setError(error?.response?.data?.msg);
    },
    onSuccess: () => {
      console.log('Upload file: Successfully uploaded');
    },
  });

  /* Callbacks */
  const handleVerifyCard = useCallback(
    async (title: string, cardNumber: string, cardId: number, file: File) => {
      if (!cardNumber || !id) return;
      setIsLoading(true);
      setError(undefined);
      try {
        const { data: fileData } = await uploadFile.mutateAsync({
          file,
        });

        await verifyCard.mutateAsync({
          cardNumber,
          title,
          cardId,
          userId: id,
          fileId: fileData.fileId,
        });
      } catch (error: any) {
        const errorMessage = error?.response?.data?.msg;
        console.error('Failed to verify card:', errorMessage);
        setError(errorMessage);
        Sentry.captureException({
          message: 'Failed to verify card',
          extra: {
            error,
          },
        });
        throw new Error(`Failed to verify card: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    },
    [id, verifyCard, uploadFile],
  );

  /* Return */
  return {
    handleVerifyCard,
    isLoading,
    error,
  };
};
